import React from "react";
import {
  X,
  FolderKanban,
  Crown,
  Infinity,
  Timer,
  CheckCircle2,
  Loader,
} from "lucide-react";

const ProjectModal = ({
  showProjectModal,
  setShowProjectModal,
  user,
  projects,
  projectName,
  setProjectName,
  projectBudget,
  setProjectBudget,
  projectDeadline,
  setProjectDeadline,
  isBudgetValid,
  isDeadlineValid,
  setIsDeadlineValid,
  validateDate,
  createProject,
  isCreating,
  showSuccess,
}) => {
  // If the modal state is false, don't render anything
  if (!showProjectModal) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-md z-[100] flex items-center justify-center p-6">
      <div className="bg-white dark:bg-slate-900 rounded-[40px] shadow-2xl p-10 w-full max-w-2xl animate-in zoom-in-95 duration-300 relative overflow-hidden border border-white/20 dark:border-slate-800">
        {/* Visual Accent */}
        <div className="absolute top-0 left-0 w-full h-2 bg-indigo-600"></div>

        <div className="flex justify-between items-start mb-8">
          <div>
            <h2 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Launch New Project
            </h2>
            <p className="text-slate-400 dark:text-slate-500 font-bold uppercase text-[10px] tracking-[0.2em] mt-1">
              Initialise Project workflow
            </p>
          </div>
          <button
            onClick={() => setShowProjectModal(false)}
            className="h-12 w-12 flex items-center justify-center text-slate-400 hover:bg-red-50 dark:hover:bg-red-500/10 hover:text-red-500 rounded-2xl transition-all"
          >
            <X size={24} />
          </button>
        </div>

        {/* --- CAPACITY INDICATOR --- */}
        {user.plan !== "businessPlus" ? (
          <div className="mb-8 p-6 bg-slate-50 dark:bg-slate-800/50 rounded-[30px] border border-slate-100 dark:border-slate-800">
            <div className="flex justify-between items-end mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700">
                  <FolderKanban
                    size={18}
                    className="text-indigo-600 dark:text-indigo-400"
                  />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500 font-black mb-0.5">
                    Current Subscription Capacity
                  </p>
                  <p className="text-lg font-black text-slate-900 dark:text-white">
                    {projects.length}{" "}
                    <span className="text-slate-300 dark:text-slate-600 font-bold">
                      / {user.plan === "pro" ? 30 : 2}
                    </span>
                  </p>
                </div>
              </div>
              <span
                className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-tighter ${(user.plan === "pro" ? 30 : 2) - projects.length <= 1
                  ? "bg-rose-100 dark:bg-rose-900/40 text-rose-600 dark:text-rose-400"
                  : "bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400"
                  }`}
              >
                {(user.plan === "pro" ? 30 : 2) - projects.length} slots left
              </span>
            </div>
            <div className="w-full h-2.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-700 ease-out ${projects.length / (user.plan === "pro" ? 30 : 2) >= 0.9
                  ? "bg-rose-500"
                  : "bg-indigo-600 dark:bg-indigo-500"
                  }`}
                style={{
                  width: `${Math.min(
                    (projects.length / (user.plan === "pro" ? 30 : 2)) * 100,
                    100
                  )}%`,
                }}
              />
            </div>
          </div>
        ) : (
          <div className="mb-8 p-4 bg-slate-50/70 dark:bg-slate-900/40 rounded-2xl border border-slate-200/60 dark:border-slate-800/80 flex items-center justify-between gap-4 backdrop-blur-sm shadow-lg">
            <div className="flex items-center gap-4">
              {/* Clean, sharp icon housing with a premium metallic accent */}
              <div className="w-10 h-10 bg-gradient-to-b from-white to-slate-50 dark:from-slate-800 dark:to-slate-800/50 rounded-xl flex items-center justify-center shadow-sm text-indigo-500 dark:text-amber-400 border border-slate-200/50 dark:border-slate-700/50">
                <Infinity size={18} strokeWidth={2} />
              </div>

              <div>
                <div className="flex items-center gap-2 mt-0.5">
                  <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 tracking-tight">
                    Business Plus+ Tier
                  </p>
                  <span className="w-1 h-1 bg-slate-300 dark:bg-slate-600 rounded-full" />
                  <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    Unlimited Project Capacity
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* --- FORM FIELDS --- */}
        <div className="space-y-6">
          <div>
            <label className="block text-[10px] font-black uppercase text-slate-400 dark:text-slate-500 tracking-widest mb-2 ml-2">
              Project Name/ID
            </label>
            <input
              autoFocus
              type="text"
              placeholder="Project Name"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="w-full px-6 py-5 bg-slate-50 dark:bg-slate-800/50 border-2 border-transparent dark:text-white rounded-2xl font-black text-slate-800 outline-none focus:border-indigo-500 dark:focus:border-indigo-500/50 transition-all placeholder:text-slate-300 dark:placeholder:text-slate-600"
              onKeyPress={(e) => e.key === "Enter" && createProject()}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black uppercase text-slate-400 dark:text-slate-500 tracking-widest mb-2 ml-2">
                Estimated Budget
              </label>
              <div className="relative flex items-center">
                <span className="absolute left-5 text-slate-300 dark:text-slate-500 font-black">
                  R
                </span>
                <input
                  type="number"
                  placeholder=""
                  value={projectBudget}
                  onChange={(e) => setProjectBudget(e.target.value)}
                  className={`w-full pl-10 pr-6 py-5 bg-slate-50 dark:bg-slate-800/50 rounded-2xl placeholder:text-slate-300 font-black text-slate-800 dark:text-white outline-none border-2 border-transparent transition-all ${!isBudgetValid && projectBudget !== ""
                    ? "border-rose-400 bg-rose-50 dark:bg-rose-900/10"
                    : "focus:border-indigo-500 dark:focus:border-indigo-500/50"
                    }`}
                />
              </div>
            </div>

            <div>
              <label className="block text-[10px] font-black uppercase text-slate-400 dark:text-slate-500 tracking-widest mb-2 ml-2">
                Completion Date (Deadline)
              </label>
              <div className="relative flex flex-col gap-2">
                <div className="relative flex items-center">
                  <Timer
                    className={`absolute left-5 transition-colors text-slate-300 duration-300 ${!isDeadlineValid && projectDeadline.length === 10
                      ? "text-rose-500"
                      : "text-slate-300 dark:text-slate-300"
                      }`}
                    size={18}
                  />
                  <input
                    type="text"
                    placeholder="DD / MM / YYYY"
                    value={projectDeadline}
                    onChange={(e) => {
                      let val = e.target.value.replace(/\D/g, "");
                      if (val.length > 2)
                        val = val.slice(0, 2) + "/" + val.slice(2);
                      if (val.length > 5)
                        val = val.slice(0, 5) + "/" + val.slice(5, 9);
                      setProjectDeadline(val);
                      if (val.length === 10) {
                        setIsDeadlineValid(validateDate(val));
                      } else {
                        setIsDeadlineValid(false);
                      }
                    }}
                    maxLength="10"
                    className={`w-full pl-12 pr-6 py-5 bg-slate-50 placeholder:text-slate-300 dark:bg-slate-800/50 rounded-2xl font-black text-slate-800 dark:text-white outline-none border-2 border-transparent transition-all ${!isDeadlineValid && projectDeadline.length === 10
                      ? "border-rose-400 bg-rose-50 dark:bg-rose-900/10"
                      : isDeadlineValid
                        ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/10"
                        : "focus:border-indigo-500 dark:focus:border-indigo-500/50"
                      }`}
                  />
                </div>
                {!isDeadlineValid && projectDeadline.length === 10 && (
                  <span className="text-[10px] font-black text-rose-500 uppercase tracking-widest ml-4 animate-in fade-in slide-in-from-top-1">
                    ⚠️ Provide a valid future date
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* --- ACTIONS --- */}
        <div className="flex gap-4 mt-10">
          {(() => {
            const isFormIncomplete =
              !projectName?.trim() ||
              !projectBudget ||
              Number(projectBudget) <= 0 ||
              projectDeadline?.length < 10;
            return (
              <button
                type="button"
                onClick={createProject}
                disabled={
                  isCreating ||
                  showSuccess ||
                  isFormIncomplete ||
                  (user.plan !== "enterprise" &&
                    projects.length >= (user.plan === "pro" ? 30 : 2))
                }
                className={`flex-[2] py-5 rounded-2xl font-black transition-all flex items-center justify-center gap-3 ${showSuccess
                  ? "bg-emerald-500 text-white shadow-xl shadow-emerald-500/20"
                  : isCreating
                    ? "bg-indigo-400 dark:bg-indigo-500/50 text-white cursor-not-allowed"
                    : isFormIncomplete
                      ? "bg-slate-100 dark:bg-slate-800 text-slate-300 dark:text-slate-600 cursor-not-allowed"
                      : "bg-indigo-600 dark:bg-indigo-500 text-white shadow-xl shadow-indigo-600/20 hover:bg-indigo-700 dark:hover:bg-indigo-400"
                  }`}
              >
                {showSuccess ? (
                  <>
                    <CheckCircle2
                      className="animate-in zoom-in"
                      size={20}
                      strokeWidth={3}
                    />
                    Submitted for Approval!
                  </>
                ) : isCreating ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    Submitting Project...
                  </>
                ) : (
                  "Submit Project"
                )}
              </button>
            );
          })()}

          <button
            type="button"
            onClick={() => setShowProjectModal(false)}
            className="flex-1 bg-white dark:bg-slate-900 border-2 border-slate-100 dark:border-slate-800 text-slate-400 dark:text-slate-500 py-5 rounded-2xl font-black uppercase text-[11px] tracking-widest hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-600 dark:hover:text-slate-300 transition-all"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;
